"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowLeft, Calculator, HelpCircle, CheckCircle2 } from "lucide-react";

export default function GstCalculator() {
  const [basePrice, setBasePrice] = useState(10000);
  const [gstRate, setGstRate] = useState(18); // default 18%
  const [taxType, setTaxType] = useState("exclusive"); // exclusive or inclusive
  const [stateType, setStateType] = useState("intra"); // intra or inter

  // Calculations
  const slab = parseFloat(gstRate);
  let computedBase = 0;
  let gstAmount = 0;
  let totalPrice = 0;

  if (taxType === "exclusive") {
    computedBase = basePrice;
    gstAmount = basePrice * (slab / 100);
    totalPrice = basePrice + gstAmount;
  } else {
    totalPrice = basePrice;
    computedBase = totalPrice / (1 + slab / 100);
    gstAmount = totalPrice - computedBase;
  }

  const cgst = stateType === "intra" ? gstAmount / 2 : 0;
  const sgst = stateType === "intra" ? gstAmount / 2 : 0;
  const igst = stateType === "inter" ? gstAmount : 0;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const gstSlabs = [
    { rate: 5, label: "5% (Household items, basic foods)" },
    { rate: 12, label: "12% (Processed foods, computers)" },
    { rate: 18, label: "18% (Services, capital goods)" },
    { rate: 28, label: "28% (Luxury goods, cars)" },
  ];

  return (
    <div className="overflow-x-hidden w-full bg-[#FAFAFA] min-h-screen pb-16">
      {/* Header Bar */}
      <div className="relative w-full border-b border-[#E5E5E5] bg-white py-6">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12 flex items-center justify-between">
          <Link
            href="/tools"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#525252] hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
          <span className="text-[12px] text-[#525252] uppercase tracking-wider font-bold bg-[#F5F5F5] px-3 py-1 rounded-full">
            Indian GST Utility
          </span>
        </div>
      </div>

      <div className="max-w-[1160px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12 py-10">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#171717] mb-2 tracking-tight">
            GST Calculator India
          </h1>
          <p className="text-sm sm:text-base text-[#525252]">
            Calculate CGST, SGST, and IGST for exclusive or inclusive prices instantly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left panel: Inputs */}
          <div className="lg:col-span-2 space-y-6">
            {/* Input card */}
            <div className="bg-white border border-[#E5E5E5] rounded-[24px] p-6 sm:p-8 shadow-sm">
              <div className="space-y-6">
                {/* Base price input */}
                <div>
                  <label className="block text-sm font-semibold text-[#171717] mb-2">
                    {taxType === "exclusive" ? "Net Base Amount (₹)" : "Total Amount Inclusive of GST (₹)"}
                  </label>
                  <div className="relative rounded-xl border border-[#E5E5E5] overflow-hidden focus-within:border-black transition-colors">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 text-lg">₹</span>
                    <input
                      type="number"
                      value={basePrice === 0 ? "" : basePrice}
                      onChange={(e) => setBasePrice(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="w-full pl-8 pr-4 py-3 bg-transparent text-lg font-bold text-[#171717] outline-none"
                      placeholder="Enter amount"
                    />
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="100000"
                    step="500"
                    value={basePrice}
                    onChange={(e) => setBasePrice(parseFloat(e.target.value))}
                    className="w-full mt-4 accent-black cursor-pointer"
                  />
                  <div className="flex justify-between text-[11px] text-neutral-400 mt-1">
                    <span>₹1,000</span>
                    <span>₹1,00,000</span>
                  </div>
                </div>

                {/* Slabs */}
                <div>
                  <label className="block text-sm font-semibold text-[#171717] mb-3">
                    Select GST Rate Slab
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {gstSlabs.map((slabObj) => (
                      <button
                        key={slabObj.rate}
                        type="button"
                        onClick={() => setGstRate(slabObj.rate)}
                        className={`py-3 px-4 rounded-xl border text-center transition-all duration-200 cursor-pointer ${
                          gstRate === slabObj.rate
                            ? "border-black bg-black text-white font-bold"
                            : "border-[#E5E5E5] bg-white text-[#171717] hover:border-neutral-400"
                        }`}
                      >
                        {slabObj.rate}%
                      </button>
                    ))}
                  </div>
                </div>

                {/* Inclusive/Exclusive Toggles */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#171717] mb-2">
                      Tax Calculation Type
                    </label>
                    <div className="flex rounded-xl border border-[#E5E5E5] p-1 bg-neutral-50">
                      <button
                        type="button"
                        onClick={() => setTaxType("exclusive")}
                        className={`flex-1 py-2 text-center text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                          taxType === "exclusive"
                            ? "bg-white text-black shadow-sm"
                            : "text-[#525252] hover:text-black"
                        }`}
                      >
                        Exclusive of GST
                      </button>
                      <button
                        type="button"
                        onClick={() => setTaxType("inclusive")}
                        className={`flex-1 py-2 text-center text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                          taxType === "inclusive"
                            ? "bg-white text-black shadow-sm"
                            : "text-[#525252] hover:text-black"
                        }`}
                      >
                        Inclusive of GST
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#171717] mb-2">
                      Transaction Mode
                    </label>
                    <div className="flex rounded-xl border border-[#E5E5E5] p-1 bg-neutral-50">
                      <button
                        type="button"
                        onClick={() => setStateType("intra")}
                        className={`flex-1 py-2 text-center text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                          stateType === "intra"
                            ? "bg-white text-black shadow-sm"
                            : "text-[#525252] hover:text-black"
                        }`}
                      >
                        Intra-State (CGST + SGST)
                      </button>
                      <button
                        type="button"
                        onClick={() => setStateType("inter")}
                        className={`flex-1 py-2 text-center text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                          stateType === "inter"
                            ? "bg-white text-black shadow-sm"
                            : "text-[#525252] hover:text-black"
                        }`}
                      >
                        Inter-State (IGST)
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Slab breakdown explanation */}
            <div className="bg-white border border-[#E5E5E5] rounded-[24px] p-6 shadow-sm">
              <h3 className="text-base font-bold text-[#171717] mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-neutral-400" />
                GST Slab Guides
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                {gstSlabs.map((slabObj) => (
                  <div key={slabObj.rate} className="flex gap-2.5 border-b border-neutral-100 pb-2">
                    <span className="font-bold text-[#171717] min-w-8">{slabObj.rate}%</span>
                    <span className="text-[#525252]">{slabObj.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right panel: Summary Output */}
          <div className="space-y-6">
            <div className="bg-neutral-900 text-white rounded-[24px] p-6 sm:p-8 shadow-md">
              <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-400 mb-6">
                Calculation Breakdown
              </h3>

              <div className="space-y-4 border-b border-neutral-800 pb-6 mb-6 text-sm">
                <div className="flex justify-between text-neutral-400">
                  <span>Base Price</span>
                  <span className="font-semibold text-white">{formatCurrency(computedBase)}</span>
                </div>

                {stateType === "intra" ? (
                  <>
                    <div className="flex justify-between text-neutral-400">
                      <span>CGST ({gstRate / 2}%)</span>
                      <span className="font-semibold text-white">{formatCurrency(cgst)}</span>
                    </div>
                    <div className="flex justify-between text-neutral-400">
                      <span>SGST ({gstRate / 2}%)</span>
                      <span className="font-semibold text-white">{formatCurrency(sgst)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-neutral-400">
                    <span>IGST ({gstRate}%)</span>
                    <span className="font-semibold text-white">{formatCurrency(igst)}</span>
                  </div>
                )}

                <div className="flex justify-between text-neutral-300 font-bold border-t border-neutral-800 pt-3">
                  <span>Total Tax (GST)</span>
                  <span className="text-white">{formatCurrency(gstAmount)}</span>
                </div>
              </div>

              <div>
                <span className="block text-[11px] uppercase tracking-wider text-neutral-400 mb-1">
                  Net Invoice Amount
                </span>
                <span className="text-3xl sm:text-4xl font-extrabold text-white">
                  {formatCurrency(totalPrice)}
                </span>
              </div>
            </div>

            {/* CTA Banner */}
            <div className="bg-white border border-[#E5E5E5] rounded-[24px] p-6 shadow-sm text-center">
              <CheckCircle2 className="w-8 h-8 text-neutral-900 mx-auto mb-3" />
              <h4 className="text-base font-bold text-[#171717] mb-2">
                Automate Invoicing & GST
              </h4>
              <p className="text-xs text-[#525252] leading-relaxed mb-6">
                Manual calculations lead to accounting errors. Use ABSS Books to automatically calculate taxes, prepare GST reports, and file compliance.
              </p>
              <Link
                href="/contact"
                className="block w-full py-2.5 bg-black text-white text-xs font-semibold rounded-lg hover:bg-neutral-800 transition-colors shadow-sm"
              >
                Try ABSS Books Free
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
